import { useMemo, useCallback } from 'react';
import useAppSelector from '@hooks/useAppSelector';
import { USER_PERMISSIONS_MAP } from '@constants/permissions/userPermissionTypes';

const EMPTY_PERMISSIONS: string[] = [];

export const useUserPermissions = () => {
    const { loaded, loading, list = EMPTY_PERMISSIONS } =
        useAppSelector(state => state.common.userPermissions);

    /**
     * Returns true only if the user has **every** supplied permission (AND semantics).
     *
     * @example
     *   hasPermission([A, B])    // true if user has A AND B
     *   hasPermission([A])       // true if user has A
     */
    const hasPermission = useCallback(
        (permissions: string[]) => permissions.every(p => list.includes(p)),
        [list],
    );

    /**
     * Returns true if the user has **any** of the supplied permissions (OR semantics).
     *
     * @example
     *   hasAnyPermission([A, B])    // true if user has A OR B
     *   hasAnyPermission([A])       // true if user has A
     *   hasAnyPermission([])        // false
     */
    const hasAnyPermission = useCallback(
        (permissions: string[]) => permissions.some(p => list.includes(p)),
        [list],
    );

    const flags = useMemo(() => ({
        canAccessTicketManagement: hasAnyPermission([
            USER_PERMISSIONS_MAP.TKM_LIST_QUEUE,
            USER_PERMISSIONS_MAP.TKM_LIST_TICKET,
            USER_PERMISSIONS_MAP.TKM_REPORT,
            USER_PERMISSIONS_MAP.TKM_MANAGE_TICKET_TYPES,
            USER_PERMISSIONS_MAP.TKM_MANAGE_EXCHANGE_ASSIGNEES,
            USER_PERMISSIONS_MAP.TKM_MANAGE_TAGS,
            USER_PERMISSIONS_MAP.TKM_MANAGE_CANNED_RESPONSE,
            USER_PERMISSIONS_MAP.TKM_EDIT_TICKET,
            USER_PERMISSIONS_MAP.MEMBER_TAG_ACCESS,
        ]),
        canManageWorkgroup: hasPermission([USER_PERMISSIONS_MAP.TKM_LIST_QUEUE]),
        canManageWorkgroupAssignees: hasPermission([USER_PERMISSIONS_MAP.TKM_LIST_QUEUE]),
        canViewTicketList: hasPermission([USER_PERMISSIONS_MAP.TKM_LIST_TICKET]),
        canViewReports: hasPermission([USER_PERMISSIONS_MAP.TKM_REPORT]),
        canManageTicketTypes: hasPermission([USER_PERMISSIONS_MAP.TKM_MANAGE_TICKET_TYPES]),
        canManageTags: hasPermission([USER_PERMISSIONS_MAP.TKM_MANAGE_TICKET_TYPES]),
        canManageExchangeAssignees: hasPermission([USER_PERMISSIONS_MAP.TKM_MANAGE_EXCHANGE_ASSIGNEES]),
        canManageCustomFields: hasPermission([USER_PERMISSIONS_MAP.TKM_MANAGE_TAGS]),
        canEditWorkgroupTag: hasAnyPermission([
            USER_PERMISSIONS_MAP.TKM_MANAGE_TAGS,
            USER_PERMISSIONS_MAP.MEMBER_TAG_ACCESS,
        ]),
        canManageCannedResponses: hasPermission([USER_PERMISSIONS_MAP.TKM_MANAGE_CANNED_RESPONSE]),
        canEditTicket: hasPermission([USER_PERMISSIONS_MAP.TKM_EDIT_TICKET]),
        canAddNewConsumer: hasPermission([USER_PERMISSIONS_MAP.ADD_NEW_CONSUMER]),
        canCancelEnrollmentReasonFraudRescission: hasPermission([
            USER_PERMISSIONS_MAP.CANCEL_ENRL_REASON_FRAUD_RESCISSION,
        ]),
    }), [hasPermission, hasAnyPermission]);

    return {
        loaded,
        loading,
        permissions: list,
        hasPermission,
        hasAnyPermission,
        ...flags,
    };
};
